import React, {memo} from 'react'

import {BrandType} from '@daedalus/core/src/brand/types'
import {useExecutionIfNotAuthenticated} from '@daedalus/core/src/auth/hooks/useExecutionIfNotAuthenticated'

import Container, {ChildrenProps} from './Container'
import SyncMessagesHistoryWithBrowserStorage from './SyncMessagesHistoryWithBrowserStorage'
import {useDeviceLayout} from '../../../../context/deviceLayout'
import {AuthDialogLoadable} from './AuthDialog'

type PasswordlessLoginPropsType = {
  brand: BrandType
  isRtl: boolean
  languageCode: string
  userCountryCode: string
  anonymousId?: string
  reCaptchaKey: string
}

export type ViewProps = ChildrenProps &
  PasswordlessLoginPropsType & {
    isMobile: boolean
  }

const PasswordlessLogin = ({
  brand,
  isRtl,
  languageCode,
  userCountryCode,
  anonymousId,
  reCaptchaKey
}: PasswordlessLoginPropsType) => {
  const {isMobile} = useDeviceLayout()

  useExecutionIfNotAuthenticated(
    async () => import(/* webpackPrefetch: true */ './AuthDialog/AuthDialog')
  )

  return (
    <>
      <SyncMessagesHistoryWithBrowserStorage />
      <Container
        languageCode={languageCode}
        anonymousId={anonymousId}
        brand={brand}
      >
        {({
          isOpen,
          authenticationFlow,
          userPhoneNumber,
          hasGenericSendErrorResponse,
          hasAuthenticationError,
          hasEnabledChannelAvailable,
          isResendMessageVisible,
          userSelectedChannel,
          enabledChannels,
          hasPhoneNumberValidationErrorResponse,
          isAttemptsByChannelMessageVisible,
          messagesAttemptsHistory,
          messageMaximumAttempts,
          isInitialStep,
          sendPasswordlessMessage,
          handleCloseOverlay,
          handleCodeSubmit,
          handleTryAgainAfterError,
          handleReSendSms,
          handleLoginClick,
          handleSocialLoginClick,
          recaptchaRef,
          selectedChannel,
          setSelectedChannel
        }) => {
          const viewProps = {
            brand,
            isRtl,
            languageCode,
            anonymousId,
            userCountryCode,
            userPhoneNumber,
            hasGenericSendErrorResponse,
            enabledChannels,
            hasAuthenticationError,
            hasEnabledChannelAvailable,
            isResendMessageVisible,
            userSelectedChannel,
            hasPhoneNumberValidationErrorResponse,
            isInitialStep,
            isAttemptsByChannelMessageVisible,
            messagesAttemptsHistory,
            messageMaximumAttempts,
            handleCloseOverlay,
            handleTryAgainAfterError,
            sendPasswordlessMessage,
            handleReSendSms,
            handleCodeSubmit,
            handleLoginClick,
            handleSocialLoginClick,
            recaptchaRef,
            selectedChannel,
            setSelectedChannel
          }

          return (
            isOpen && (
              <AuthDialogLoadable
                recaptchaRef={recaptchaRef}
                reCaptchaKey={reCaptchaKey}
                isMobile={isMobile}
                brand={brand}
                isOpen={isOpen}
                handleCloseOverlay={handleCloseOverlay}
                hasAuthenticationError={hasAuthenticationError}
                handleTryAgainAfterError={handleTryAgainAfterError}
                viewProps={viewProps}
                authenticationFlow={authenticationFlow}
              />
            )
          )
        }}
      </Container>
    </>
  )
}

export default memo<PasswordlessLoginPropsType>(PasswordlessLogin)
